import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { dnAlert } from "./utils/dNews_Alert";
import { apiFetch } from "./utils/api";
import { FaPlus, FaEdit, FaTrash, FaSearch, FaTimes, FaGripVertical } from "react-icons/fa";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./Payslip_Components.css";

function Payslip_Components({ navSize }) {
    const navigate = useNavigate();

    const [components, setComponents] = useState([]);
    const [loading, setLoading] = useState(true);

    // Separate add inputs per column (type is implicit per column)
    const [newEarning, setNewEarning] = useState("");
    const [newDeduction, setNewDeduction] = useState("");
    const [adding, setAdding] = useState(false);

    // Per-column order: "Manual" = the saved display_order (drag-free up/down
    // reordering), or "A-Z"/"Z-A" name sort (view only, no reorder buttons).
    const [earnSort, setEarnSort] = useState("Manual");
    const [dedSort, setDedSort] = useState("Manual");

    // Per-column search. These lists grow long (20+ each), so filtering by
    // name is quicker than scrolling. Kept per-column so searching Earnings
    // doesn't hide Deductions.
    const [earnQuery, setEarnQuery] = useState("");
    const [dedQuery, setDedQuery] = useState("");

    const filterList = (list, query) => {
        const q = query.trim().toLowerCase();
        if (!q) return list;
        return list.filter((c) => (c.component_name || "").toLowerCase().includes(q));
    };

    const sortList = (list, dir) => {
        if (dir === "Manual") return [...list];   // keep saved display_order
        const arr = [...list].sort((a, b) =>
            (a.component_name || "").localeCompare(b.component_name || "", undefined, { sensitivity: "base" })
        );
        return dir === "Z-A" ? arr.reverse() : arr;
    };

    // Persist a new order (auto-saves - no button). Silent refetch on success.
    const persistOrder = async (reordered) => {
        try {
            const res = await apiFetch(`/api/payslip/components/reorder`, {
                method: "PUT", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ordered_ids: reordered.map((c) => c.component_id) }),
            });
            if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || "Reorder failed"); }
            fetchComponents();
        } catch (err) { dnAlert.error("Couldn't reorder", err.message); }
    };

    // Drag-and-drop reordering within a column (Manual mode only).
    const [dragItem, setDragItem] = useState(null);   // { type, index }
    const onDropRow = (list, type, dropIndex) => {
        const info = dragItem;
        setDragItem(null);
        if (!info || info.type !== type || info.index === dropIndex) return;
        const reordered = [...list];
        const [moved] = reordered.splice(info.index, 1);
        reordered.splice(dropIndex, 0, moved);
        persistOrder(reordered);
    };

    // ── Auth check (Admin / Sub Admin only, like the other dSlip pages) ──
    useEffect(() => {
        apiFetch("/api/employee/logined_employee", { method: "GET", credentials: "include" })
            .then((r) => r.json())
            .then((data) => {
                if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                    navigate("/login");
                    return;
                }
                // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
            })
            .catch(() => navigate("/login"));
    }, [navigate]);

    // ── Fetch components ──
    // showLoader=true only on the initial mount. Mutations (add/edit/delete)
    // refetch silently so the columns stay mounted and don't flash/collapse.
    const fetchComponents = async ({ showLoader = false } = {}) => {
        if (showLoader) setLoading(true);
        try {
            const res = await apiFetch("/api/payslip/components");
            const data = await res.json();
            setComponents(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("❌ fetchComponents error:", err);
        } finally {
            if (showLoader) setLoading(false);
        }
    };

    useEffect(() => {
        fetchComponents({ showLoader: true });
    }, []);

    // ── Add (type set by which column the add box belongs to) ──
    const handleAdd = async (type) => {
        const name = (type === "Earning" ? newEarning : newDeduction).trim();
        if (!name) {
            dnAlert.warning("Required", "Component name cannot be empty.");
            return;
        }
        setAdding(true);
        try {
            const res = await apiFetch("/api/payslip/components", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ component_name: name, component_type: type }),
            });
            const data = await res.json();
            if (data.success) {
                if (type === "Earning") setNewEarning(""); else setNewDeduction("");
                fetchComponents();
            } else {
                dnAlert.error("Error", data.error || "Could not add component.");
            }
        } catch {
            dnAlert.error("Error", "Network error. Please try again.");
        } finally {
            setAdding(false);
        }
    };

    // ── Edit name (type stays - determined by the column) ──
    const handleEdit = async (comp) => {
        const name = await dnAlert.prompt({
            title: "Rename Component",
            defaultValue: comp.component_name,
            confirmLabel: "Save",
            validate: (v) => (!v || !v.trim() ? "Component name cannot be empty" : undefined),
        });
        if (!name || name.trim() === comp.component_name) return;

        try {
            const res = await apiFetch(`/api/payslip/components/${comp.component_id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ component_name: name.trim(), component_type: comp.component_type }),
            });
            const data = await res.json();
            if (data.success) {
                fetchComponents();
            } else {
                dnAlert.error("Error", data.error || "Could not update component.");
            }
        } catch {
            dnAlert.error("Error", "Network error. Please try again.");
        }
    };

    // ── Delete ──
    const handleDelete = async (comp) => {
        const ok = await dnAlert.confirm({
            title: "Delete Component?",
            message: `Delete ${comp.component_name}? This cannot be undone.`,
            danger: true,
            confirmLabel: "Yes, delete",
        });
        if (!ok) return;

        try {
            const res = await apiFetch(`/api/payslip/components/${comp.component_id}`, { method: "DELETE" });
            const data = await res.json();
            if (data.success) {
                fetchComponents();
            } else {
                dnAlert.error("Error", data.error || "Could not delete component.");
            }
        } catch {
            dnAlert.error("Error", "Network error. Please try again.");
        }
    };

    const allEarnings = sortList(components.filter((c) => c.component_type === "Earning"), earnSort);
    const allDeductions = sortList(components.filter((c) => c.component_type === "Deduction"), dedSort);
    const earnings = filterList(allEarnings, earnQuery);
    const deductions = filterList(allDeductions, dedQuery);

    // ── One column (Earnings or Deductions) ──
    const renderColumn = (title, type, list, newValue, setNewValue, accentClass, sortValue, setSort, query, setQuery, totalCount) => (
        <div className="pc-col">
            <div className={`pc-col-header ${accentClass}`}>
                <span className="pc-col-title">{title}</span>

                {/* Search sits inline in the header rather than on its own row,
                    so the column keeps as much vertical space as possible. */}
                <div className="pc-col-search">
                    <FaSearch className="pc-col-search-icon" />
                    <input
                        type="text"
                        className="pc-col-search-input"
                        placeholder="Filter…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Escape") setQuery(""); }}
                    />
                    {query && (
                        <button
                            type="button"
                            className="pc-col-search-clear"
                            title="Clear filter"
                            onClick={() => setQuery("")}
                        >
                            <FaTimes />
                        </button>
                    )}
                </div>

                <div className="pc-col-header-right">
                    <select
                        className="pc-col-sort"
                        value={sortValue}
                        onChange={(e) => setSort(e.target.value)}
                        title="Order"
                    >
                        <option value="Manual">Manual</option>
                        <option value="A-Z">A-Z</option>
                        <option value="Z-A">Z-A</option>
                    </select>
                    {/* While filtering, show matches/total so it's obvious the
                        list is filtered and not that rows went missing. */}
                    <span className="pc-col-count" title={query.trim() ? `${list.length} of ${totalCount} shown` : `${totalCount} total`}>
                        {query.trim() ? `${list.length}/${totalCount}` : totalCount}
                    </span>
                </div>
            </div>

            <div className="pc-col-add">
                <input
                    type="text"
                    className="pc-input"
                    placeholder={`Add ${title.slice(0, -1).toLowerCase()}…`}
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleAdd(type); }}
                />
                <button className="pc-add-btn" onClick={() => handleAdd(type)} disabled={adding}>
                    <FaPlus />
                </button>
            </div>

            <div className="pc-col-list">
                {list.length === 0 ? (
                    query.trim()
                        ? <div className="pc-empty">No {title.toLowerCase()} match “{query.trim()}”.</div>
                        : <div className="pc-empty">No {title.toLowerCase()} yet. Add one above.</div>
                ) : (
                    list.map((comp, i) => {
                        const canReorder = sortValue === "Manual" && !query.trim();
                        const dragging = dragItem && dragItem.type === type && dragItem.index === i;
                        return (
                        <div
                            className={`pc-row ${canReorder ? "pc-row-draggable" : ""} ${dragging ? "pc-row-dragging" : ""}`}
                            key={comp.component_id}
                            draggable={canReorder}
                            onDragStart={canReorder ? () => setDragItem({ type, index: i }) : undefined}
                            onDragEnd={canReorder ? () => setDragItem(null) : undefined}
                            onDragOver={canReorder ? (e) => e.preventDefault() : undefined}
                            onDrop={canReorder ? () => onDropRow(list, type, i) : undefined}
                        >
                            {canReorder && <span className="pc-row-grip" title="Drag to reorder"><FaGripVertical /></span>}
                            <span className="pc-row-num">{i + 1}</span>
                            <span className="pc-row-name">{comp.component_name}</span>
                            <div className="pc-row-actions">
                                <button className="pc-icon-btn pc-icon-edit" title="Rename" onClick={() => handleEdit(comp)}>
                                    <FaEdit />
                                </button>
                                <button className="pc-icon-btn pc-icon-delete" title="Delete" onClick={() => handleDelete(comp)}>
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                        );
                    })
                )}
            </div>
        </div>
    );

    return (
        <div className="pc-container">
            <div className={`pc-dashboard ${navSize}`}>
                <div className="pc-content">

                    {/* Header */}
                    <div className="pc-page-header">
                        <h2>
                            <a className="navbar-brand" href="#">
                                <img src={logo_eagle} alt="dAdmin Logo" style={{ height: "50px", objectFit: "contain", marginLeft: "-20px", marginRight: "10px" }} />
                            </a>
                            Payslip Components
                        </h2>
                        <p className="pc-subtitle">
                            Manage the Earnings and Deductions line items available when building salary structures.
                        </p>
                    </div>

                    {loading ? (
                        <div className="pc-loading-card">Loading components…</div>
                    ) : (
                        <div className="pc-grid">
                            {renderColumn("Earnings", "Earning", earnings, newEarning, setNewEarning, "pc-accent-green", earnSort, setEarnSort, earnQuery, setEarnQuery, allEarnings.length)}
                            {renderColumn("Deductions", "Deduction", deductions, newDeduction, setNewDeduction, "pc-accent-red", dedSort, setDedSort, dedQuery, setDedQuery, allDeductions.length)}
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}

export default Payslip_Components;
